import React, { createContext, useContext, useEffect, useState } from "react";
import { API, apiGet, apiSend, getToken, setToken, clearToken, CONN_ERR } from "./api";
import { TERMS_VERSION } from "./components/Terms";
import { setPdfContext } from "./export";

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = async () => {
    try {
      const me = await apiGet("/auth/me");
      setUser(me);
      setPdfContext({
        company: me.company_name,
        terms: { version: me.terms_accepted_version, acceptedAt: me.terms_accepted_at, acceptedBy: me.display_name || me.company_name },
      });
    } catch {
      setUser(null);
    }
  };

  useEffect(() => {
    (async () => {
      if (getToken()) await loadMe();
      setLoading(false);
    })();
  }, []);

  const finishAuth = async (token) => {
    setToken(token);
    await loadMe();
  };

  const login = async (identifier, password) => {
    let res;
    try {
      res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, password }),
      });
    } catch (e) {
      throw new Error(CONN_ERR);
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(res.status >= 500 ? CONN_ERR : (data?.detail || "Login failed"));
    await finishAuth(data.access_token);
  };

  const register = async (email, password, company) => {
    let res;
    try {
      res = await fetch(`${API}/auth/register-owner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, company_name: company, terms_version: TERMS_VERSION }),
      });
    } catch (e) {
      throw new Error(CONN_ERR);
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(res.status >= 500 ? CONN_ERR : (data?.detail || "Registration failed"));
    await finishAuth(data.access_token);
  };

  const acceptTerms = async () => {
    await apiSend("/terms/accept", "POST", { version: TERMS_VERSION });
    await loadMe();
  };

  const logout = async () => {
    clearToken();
    setUser(null);
  };

  return (
    <Ctx.Provider
      value={{
        user,
        loading,
        isManager: user?.role === "owner" || user?.role === "manager",
        login,
        register,
        acceptTerms,
        logout,
        refresh: loadMe,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}
